import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { useTheme } from '../theme/ThemeContext';
import { usePermissions } from '../context/PermissionContext';
import { ROUTES } from '../constants/routes';
import useBedInventoryStore, { selectBoardTree, selectChildCounts, selectInventoryStats } from '../stores/bedInventoryStore';
import useBedCategoryStore from '../stores/bedCategoryStore';
import useDepartmentStore from '../stores/departmentStore';
import AdminPageHeader from '../components/common/AdminPageHeader';
import InventoryStatsStrip from '../components/bedboard/InventoryStatsStrip';
import FeesBillingTabs from '../components/settings/FeesBillingTabs';
import { ConfirmationModal } from '../components/common';
import { ORG_WIDE_FILTER, filterByDeptMembership } from '../utils/deptMembershipFilter';
import './ProcedureCatalogPage.css';

// B3 / S5 — Bed & Tariff Setup builder. A hierarchical ward→room→bed tree
// (NOT a flat catalog). B6 — a ward is taggable to 0/1/N departments via the
// shared "Visible to" multi-select (junction; scalar department_id dropped); a
// room pins ONE B2 bed-category (rate/tags resolve LIVE, never copied); beds
// inherit the room's category+rate locked. Bay/open-ward beds attach via an
// is_bay room (FE-2b). Behind the B1 admission gate + MANAGE_BED_CONFIG.

/** Resolve a category row (from the B2 store) → locked rate/tags preview text. */
export function categoryPreview(cat) {
  if (!cat) return null;
  const parts = [`₹${Number(cat.per_day_charge || 0)} / day`];
  if (cat.nursing_charge_per_day != null) parts.push(`Nursing ₹${Number(cat.nursing_charge_per_day)}`);
  if (cat.rmo_charge_per_day != null) parts.push(`RMO ₹${Number(cat.rmo_charge_per_day)}`);
  parts.push(`GST ${cat.gst_rate ?? 0}%`);
  const tags = [];
  if (cat.care_level) tags.push(cat.care_level);
  if (cat.tier) tags.push(cat.tier);
  if (cat.ac) tags.push('AC');
  if (cat.sharing != null) tags.push(`${cat.sharing}-bed`);
  return { rate: parts.join(' · '), tags };
}

/** Suggested bed count from the category's sharing (soft; absent → 1). */
export const suggestBedCount = (cat) => (cat && cat.sharing != null ? Number(cat.sharing) : 1);

export default function BedSetupPage() {
  const { isDark } = useTheme();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();

  const {
    wards, rooms, beds, isLoading, error, includeInactive,
    hydrate, createWard, updateWard, deactivateWard, reactivateWard,
    setWardDepartments,
    createRoom, updateRoom, deactivateRoom, reactivateRoom,
    createBeds, updateBed, deactivateBed, reactivateBed,
    setIncludeInactive, clearError,
  } = useBedInventoryStore();

  const bedCategories = useBedCategoryStore((s) => s.bedCategories);
  const fetchBedCategories = useBedCategoryStore((s) => s.fetchBedCategories);
  const departments = useDepartmentStore((s) => s.departments);
  const fetchDepartments = useDepartmentStore((s) => s.fetchDepartments);

  // ── Forms ──────────────────────────────────────────────────────────
  const [wardForm, setWardForm] = useState(null);   // null | {mode,id?}
  const [wardRow, setWardRow] = useState({ name: '' });
  const [wardDeptIds, setWardDeptIds] = useState([]); // B6 "Visible to" chips
  const [wardOrig, setWardOrig] = useState(null);
  const [wardErr, setWardErr] = useState({});

  const [roomForm, setRoomForm] = useState(null);   // null | {mode, wardId, id?}
  const [roomRow, setRoomRow] = useState({ name: '', bedcat_id: '' });
  const [roomOrig, setRoomOrig] = useState(null);
  const [roomErr, setRoomErr] = useState({});

  const [expanded, setExpanded] = useState({});     // ward_id → bool
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);
  const [deptFilter, setDeptFilter] = useState(''); // B6 ward-list dept filter

  // Bed + bay + deactivate state (FE-2b).
  const [bedForm, setBedForm] = useState(null);     // {mode,wardId,roomId,id?}
  const [bedRow, setBedRow] = useState({ count: 1, label: '', bedcat_id: '' });
  const [bedErr, setBedErr] = useState({});
  const [confirm, setConfirm] = useState(null);

  // B6 / S4 (Variant A) — ONE inline form open at a time per page. Every
  // form-OPENING affordance (Edit / + Add) is disabled while any form is open,
  // so a sibling can't be triggered mid-edit (OQ-4 = YES per the mockup).
  const formOpen = !!wardForm || !!roomForm || !!bedForm;

  // Initial load — stable deps (zustand action identities), single-flight stores.
  useEffect(() => { hydrate(); }, [hydrate]);
  useEffect(() => { fetchBedCategories(); }, [fetchBedCategories]);
  useEffect(() => { fetchDepartments(); }, [fetchDepartments]);

  // ── Derived (single source of truth) ───────────────────────────────
  const tree = useMemo(
    () => selectBoardTree({ wards, rooms, beds }),
    [wards, rooms, beds],
  );

  // B7 — ONE shared selector for every config count on this page: the org
  // summary strip AND each ward card's bed count. ACTIVE-only, org-wide (NOT the
  // dept-filtered subset — the filtered "shown" count below stays distinct, RC-5).
  const stats = useMemo(
    () => selectInventoryStats({ wards, rooms, beds }),
    [wards, rooms, beds],
  );

  // B6 / S3 — ward-list dept filter, MEMBERSHIP semantic via the SHARED util
  // (6th consumer, zero re-implementation): a dept anchor shows ONLY wards whose
  // dept-set CONTAINS it (org-wide excluded); "Org-wide" shows ONLY empty-set
  // wards (a dead-only DEPARTMENT ward lands here, captioned); "" = every ward.
  // Composes AND with `includeInactive` (already applied by the store fetch).
  const visibleTree = useMemo(() => {
    if (!deptFilter) return tree;
    const allowed = new Set(
      filterByDeptMembership(tree.map((t) => t.ward), deptFilter).map((w) => w.ward_id),
    );
    return tree.filter((t) => allowed.has(t.ward.ward_id));
  }, [tree, deptFilter]);

  // ACTIVE B2 categories only — a soft-deleted category is not a pickable option.
  const activeCategories = useMemo(
    () => (bedCategories || []).filter((c) => c.active !== false),
    [bedCategories],
  );
  const categoryById = useMemo(() => {
    const m = {};
    for (const c of bedCategories || []) m[c.bed_category_id] = c;
    return m;
  }, [bedCategories]);
  const hasCategories = activeCategories.length > 0;

  // B6 — VISIBILITY scope over the ward's dept-SET (was a scalar). The room/bay
  // bed-category picker offers categories reachable from the ward: org-wide /
  // untagged ∪ categories whose department_ids intersect ANY of the ward's
  // depts. An org-wide ward (empty dept-set) shows ALL categories. A DEPARTMENT
  // category with ZERO active edges (dead-only) is treated org-wide → shown
  // (RC-4 empty-set carve-out). `selectedId` keeps a currently-selected value
  // visible on edit (no silent clear). ONE-WAY: the picker never drives the
  // ward's dept-set.
  const scopedCategories = (wardDeptIds, selectedId) => {
    if (!wardDeptIds || wardDeptIds.length === 0) return activeCategories;
    return activeCategories.filter((c) =>
      c.owner_scope !== 'DEPARTMENT'
      || (c.department_ids || []).length === 0
      || (c.department_ids || []).some((d) => wardDeptIds.includes(d))
      || c.bed_category_id === selectedId,
    );
  };

  // ACTIVE departments only (dept-less org → field hidden). A ward tagged to a
  // now-dead dept keeps the id but the option isn't offered — R-B3-2.
  const activeDepts = useMemo(
    () => (departments || [])
      .filter((d) => d.active !== false)
      .map((d) => ({ dept_id: d.dept_id || d.id, name: d.name })),
    [departments],
  );
  const deptNameById = useMemo(() => {
    const m = {};
    for (const d of activeDepts) m[d.dept_id] = d.name;
    return m;
  }, [activeDepts]);
  const hasDepartments = activeDepts.length > 0;

  // B6 — SINGLE SOURCE OF TRUTH for a ward's dept-tag display (card chips +
  // room-form inherited line + edit-form seed). `department_ids` carries only
  // ACTIVE tags (server intersects dead edges out); [] with owner_scope
  // DEPARTMENT = tags retired → org-wide caption (RC-4 / §0 dead-tag parity).
  const wardDeptView = (ward) => {
    const ids = ward.department_ids || [];
    return {
      ids,
      names: ids.map((id) => deptNameById[id] || id),
      retired: ids.length === 0 && ward.owner_scope === 'DEPARTMENT',
      orgWide: ids.length === 0,
    };
  };
  const wardDeptText = (ward) => {
    const v = wardDeptView(ward);
    if (v.names.length > 0) return v.names.join(', ');
    return v.retired ? 'Org-wide (tagged departments retired)' : 'Org-wide';
  };

  // ── Ward form ──────────────────────────────────────────────────────
  const openAddWard = () => {
    setWardRow({ name: '' });
    setWardDeptIds([]);
    setWardOrig(null);
    setWardErr({});
    setBanner(null);
    setWardForm({ mode: 'add' });
  };
  const openEditWard = (w) => {
    // Seed chips from the ward's ACTIVE tags (dead edges already intersected out
    // server-side). A name-only save never touches raw edges — the PUT fires
    // only when the chips changed vs seeded (AC3 dead-tag never auto-cleared).
    setWardRow({ name: w.name || '' });
    setWardDeptIds(Array.isArray(w.department_ids) ? [...w.department_ids] : []);
    setWardOrig(w);
    setWardErr({});
    setBanner(null);
    setWardForm({ mode: 'edit', id: w.ward_id });
  };
  const closeWard = () => { setWardForm(null); setWardOrig(null); setWardErr({}); setWardDeptIds([]); };
  const toggleWardDept = (deptId) => {
    setWardDeptIds((prev) =>
      prev.includes(deptId) ? prev.filter((d) => d !== deptId) : [...prev, deptId]);
  };

  const submitWard = async () => {
    const e = {};
    if (!wardRow.name.trim()) e.name = 'Required';
    setWardErr(e);
    if (Object.keys(e).length) return;

    const isAdd = wardForm.mode === 'add';
    // The dept-set travels ONLY via the departments PUT — the create/PATCH body
    // carries `name` only (owner_scope/department_ids stripped, extra=forbid).
    const seeded = isAdd ? [] : (Array.isArray(wardOrig.department_ids) ? wardOrig.department_ids : []);
    const deptChanged =
      wardDeptIds.length !== seeded.length || wardDeptIds.some((d) => !seeded.includes(d));

    setBusy(true);
    let result;
    if (isAdd) {
      result = await createWard({ name: wardRow.name.trim() });
    } else {
      const patch = {};
      if (wardRow.name.trim() !== wardOrig.name) patch.name = wardRow.name.trim();
      // "No changes" only when BOTH the name and the chips are unchanged.
      if (Object.keys(patch).length === 0 && !deptChanged) {
        setBusy(false); setBanner({ kind: 'err', msg: 'No changes to save' }); return;
      }
      result = Object.keys(patch).length
        ? await updateWard(wardForm.id, patch)
        : { success: true, data: {} }; // dept-tag-only change → skip the PATCH
    }

    if (!result.success) {
      setBusy(false);
      if (result.code === 'WARD_NAME_TAKEN') { setWardErr({ name: result.error || 'Ward name already exists' }); return; }
      setBanner({ kind: 'err', msg: result.error || 'Save failed' });
      return;
    }

    // Tag departments via the PUT on the SAME Save click, AFTER create/PATCH.
    // Create: only when ≥1 chip. Edit: only when chips changed vs seeded.
    const targetId = isAdd ? result.data?.ward_id : wardForm.id;
    if (deptChanged) {
      if (!targetId) {
        setBusy(false);
        setBanner({ kind: 'err', msg: `Ward ${isAdd ? 'created' : 'updated'}, but departments could not be saved — edit it to set departments.` });
        closeWard();
        return;
      }
      const deptResult = await setWardDepartments(targetId, wardDeptIds);
      setBusy(false);
      if (!deptResult.success) {
        setBanner({ kind: 'err', msg: `Ward ${isAdd ? 'created' : 'updated'}, but departments were not saved: ${deptResult.error || 'unknown error'}` });
        return;
      }
    } else {
      setBusy(false);
    }
    closeWard();
    setBanner({ kind: 'ok', msg: isAdd ? 'Ward created' : 'Ward updated' });
  };

  // ── Room form ──────────────────────────────────────────────────────
  const openAddRoom = (wardId) => {
    setRoomRow({ name: '', bedcat_id: '' });
    setRoomOrig(null);
    setRoomErr({});
    setBanner(null);
    setRoomForm({ mode: 'add', wardId });
  };
  const openEditRoom = (wardId, r) => {
    setRoomRow({ name: r.name || '', bedcat_id: r.bedcat_id || '' });
    setRoomOrig(r);
    setRoomErr({});
    setBanner(null);
    setRoomForm({ mode: 'edit', wardId, id: r.room_id });
  };
  const closeRoom = () => { setRoomForm(null); setRoomOrig(null); setRoomErr({}); };

  const roomCat = roomForm ? categoryById[roomRow.bedcat_id] : null;
  const roomPreview = categoryPreview(roomCat);

  const submitRoom = async () => {
    const e = {};
    if (!roomRow.name.trim()) e.name = 'Required';
    if (!roomRow.bedcat_id) e.bedcat_id = 'Pick a bed category';
    setRoomErr(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    let result;
    if (roomForm.mode === 'add') {
      result = await createRoom(roomForm.wardId, {
        name: roomRow.name.trim(),
        bedcat_id: roomRow.bedcat_id,
      });
    } else {
      const patch = {};
      if (roomRow.name.trim() !== roomOrig.name) patch.name = roomRow.name.trim();
      if (roomRow.bedcat_id !== roomOrig.bedcat_id) patch.bedcat_id = roomRow.bedcat_id;
      if (Object.keys(patch).length === 0) {
        setBusy(false); setBanner({ kind: 'err', msg: 'No changes to save' }); return;
      }
      result = await updateRoom(roomForm.wardId, roomForm.id, patch);
    }
    setBusy(false);
    if (!result.success) {
      if (result.code === 'ROOM_NAME_TAKEN') { setRoomErr({ name: result.error || 'Room name already used in this ward' }); return; }
      setBanner({ kind: 'err', msg: result.error || 'Save failed' });
      return;
    }
    const wasAdd = roomForm.mode === 'add';
    closeRoom();
    setBanner({ kind: 'ok', msg: wasAdd ? 'Room created' : 'Room updated' });
  };

  // ══ FE-2b: bed section / bay flow / deactivate confirms ══
  const openAddBeds = (wardId, roomId, cat) => {
    setBedRow({ count: suggestBedCount(cat), label: '', bedcat_id: '' });
    setBedErr({}); setBanner(null);
    setBedForm({ mode: 'addBeds', wardId, roomId });
  };
  const openAddBay = (wardId) => {
    setBedRow({ count: 1, label: '', bedcat_id: '' });
    setBedErr({}); setBanner(null);
    setBedForm({ mode: 'addBay', wardId });
  };
  const openRenameBed = (wardId, roomId, bed) => {
    setBedRow({ count: 1, label: bed.label || '', bedcat_id: '' });
    setBedErr({}); setBanner(null);
    setBedForm({ mode: 'editBed', wardId, roomId, id: bed.bed_id });
  };
  const closeBed = () => { setBedForm(null); setBedErr({}); };

  const bedFormCat = bedForm?.mode === 'addBay' ? categoryById[bedRow.bedcat_id] : null;
  const bedFormPreview = categoryPreview(bedFormCat);
  // A second bay in the SAME ward needs an explicit name (a bare second bay
  // would collide with the server's "General Bay" default → 409).
  const wardBayCount = bedForm?.mode === 'addBay'
    ? rooms.filter((r) => r.ward_id === bedForm.wardId && r.is_bay).length
    : 0;
  const baySecondNeedsName = wardBayCount > 0;

  const submitBed = async () => {
    setBusy(true);
    let result;
    if (bedForm.mode === 'addBay') {
      if (!bedRow.bedcat_id) { setBusy(false); setBedErr({ bedcat_id: 'Pick a bed category' }); return; }
      if (baySecondNeedsName && !bedRow.label.trim()) { setBusy(false); setBedErr({ label: 'Name this bay' }); return; }
      // 1) create the is_bay room (name optional → server defaults "General Bay")
      const roomBody = { bedcat_id: bedRow.bedcat_id, is_bay: true };
      if (bedRow.label.trim()) roomBody.name = bedRow.label.trim();
      const roomResult = await createRoom(bedForm.wardId, roomBody);
      if (!roomResult.success) {
        setBusy(false);
        if (roomResult.code === 'ROOM_NAME_TAKEN') { setBedErr({ label: roomResult.error || 'Bay name already used in this ward' }); return; }
        setBanner({ kind: 'err', msg: roomResult.error || 'Could not create bay' });
        return;
      }
      // 2) bulk-create beds under the bay room
      result = await createBeds(bedForm.wardId, roomResult.data.room_id, { count: Number(bedRow.count) || 1 });
    } else if (bedForm.mode === 'addBeds') {
      result = await createBeds(bedForm.wardId, bedForm.roomId, { count: Number(bedRow.count) || 1 });
    } else { // editBed — rename
      if (!bedRow.label.trim()) { setBusy(false); setBedErr({ label: 'Required' }); return; }
      result = await updateBed(bedForm.wardId, bedForm.roomId, bedForm.id, { label: bedRow.label.trim() });
    }
    setBusy(false);
    if (!result.success) {
      if (result.code === 'BED_LABEL_TAKEN') { setBedErr({ label: result.error || 'Bed label already used in this room' }); return; }
      if (result.code === 'BED_BULK_LIMIT') {
        const cap = result.max ?? 50;
        setBanner({ kind: 'err', msg: `Max ${cap} beds per add — add again for more.` });
        return;
      }
      setBanner({ kind: 'err', msg: result.error || 'Save failed' });
      return;
    }
    closeBed();
    setBanner({ kind: 'ok', msg: bedForm.mode === 'editBed' ? 'Bed renamed' : 'Beds added' });
  };

  // ── Deactivate / reactivate (children-present confirm from the shared selector) ──
  const askDeactivateWard = (ward) => {
    const c = selectChildCounts({ wards, rooms, beds }, ward.ward_id);
    setConfirm({
      title: `Deactivate ${ward.name}?`,
      message: `${c.rooms} rooms / ${c.beds} beds will be hidden. Reactivating restores them.`,
      onConfirm: async () => {
        setConfirm(null);
        const r = await deactivateWard(ward.ward_id);
        setBanner(r.success ? { kind: 'ok', msg: 'Ward deactivated' } : { kind: 'err', msg: r.error || 'Deactivate failed' });
      },
    });
  };
  const askDeactivateRoom = (wardId, room) => {
    const c = selectChildCounts({ wards, rooms, beds }, room.room_id);
    setConfirm({
      title: `Deactivate ${room.name}?`,
      message: `${c.beds} beds will be hidden. Reactivating restores them.`,
      onConfirm: async () => {
        setConfirm(null);
        const r = await deactivateRoom(wardId, room.room_id);
        setBanner(r.success ? { kind: 'ok', msg: 'Room deactivated' } : { kind: 'err', msg: r.error || 'Deactivate failed' });
      },
    });
  };
  const doDeactivateBed = async (wardId, roomId, bed) => {
    const r = await deactivateBed(wardId, roomId, bed.bed_id);
    setBanner(r.success ? { kind: 'ok', msg: 'Bed deactivated' } : { kind: 'err', msg: r.error || 'Deactivate failed' });
  };

  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';
  const inputCls =
    'w-full rounded-lg px-3 py-2 text-sm border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100';
  const labelCls =
    'block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1';

  // ── Bed section (shared by ordinary rooms AND bays) ─────────────────
  // Beds hold NO own category — they resolve their category+rate LIVE via the
  // room's bedcat_id (S3 AC2). Changing the room category re-prices every bed
  // by construction (live-inherit).
  const renderBedSection = (wardId, room, roomBeds, { isBay = false } = {}) => {
    const cat = categoryById[room.bedcat_id];
    const prev = categoryPreview(cat);
    const rateText = cat
      ? `${cat.name} · ${prev ? prev.rate : ''}`
      : '— (category unavailable)';
    return (
      <div className="mt-2 pl-4 flex flex-col gap-1">
        {roomBeds.map((bed) => (
          <div
            key={bed.bed_id}
            className={`flex items-center justify-between gap-2 text-xs rounded px-2 py-1 ${bed.active === false ? 'opacity-70' : ''} bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700`}
          >
            {bedForm && bedForm.mode === 'editBed' && bedForm.id === bed.bed_id ? (
              <div className="flex items-center gap-2 flex-1">
                <input
                  className={inputCls}
                  aria-label="Bed label"
                  value={bedRow.label}
                  onChange={(e) => setBedRow((r) => ({ ...r, label: e.target.value }))}
                />
                {bedErr.label && <span className="text-rose-600">{bedErr.label}</span>}
                <button className="pcp-btn pcp-btn-primary" onClick={submitBed} disabled={busy}>Save</button>
                <button className="pcp-btn pcp-btn-secondary" onClick={closeBed} disabled={busy}>Cancel</button>
              </div>
            ) : (
              <>
                <span className="text-slate-700 dark:text-slate-200">
                  Bed {bed.label}
                  <span className="text-slate-400 ml-2">{rateText} (locked)</span>
                  {bed.active === false && <span className="pcp-chip pcp-chip-inactive ml-2">Deactivated</span>}
                </span>
                <span className="pcp-actions">
                  <button className="pcp-btn pcp-btn-link" onClick={() => openRenameBed(wardId, room.room_id, bed)} disabled={formOpen}>Rename</button>
                  {bed.active === false
                    ? <button className="pcp-btn pcp-btn-link" onClick={() => reactivateBed(wardId, room.room_id, bed.bed_id)} disabled={formOpen}>Reactivate</button>
                    : <button className="pcp-btn pcp-btn-link pcp-btn-danger-link" onClick={() => doDeactivateBed(wardId, room.room_id, bed)} disabled={formOpen}>Deactivate</button>}
                </span>
              </>
            )}
          </div>
        ))}

        {/* Bulk add-beds form (count pre-filled from the room's category sharing) */}
        {bedForm && bedForm.mode === 'addBeds' && bedForm.roomId === room.room_id ? (
          <div className="flex items-center gap-2 mt-1">
            <label className="text-xs text-slate-600 dark:text-slate-300">How many beds?</label>
            <input
              className={inputCls}
              type="number"
              min="1"
              max="50"
              aria-label="Bed count"
              value={bedRow.count}
              onChange={(e) => setBedRow((r) => ({ ...r, count: e.target.value }))}
              style={{ width: 90 }}
            />
            <button className="pcp-btn pcp-btn-primary" onClick={submitBed} disabled={busy}>Add</button>
            <button className="pcp-btn pcp-btn-secondary" onClick={closeBed} disabled={busy}>Cancel</button>
          </div>
        ) : (
          <button
            className="pcp-btn pcp-btn-link self-start"
            onClick={() => openAddBeds(wardId, room.room_id, cat)}
            disabled={formOpen}
          >
            {isBay ? '+ Add bay beds' : '+ Add beds'}
          </button>
        )}
      </div>
    );
  };

  // B6 / S4 (Variant A) — the ward add/edit form panel. Rendered at the TOP for
  // a new ward ({mode:'add'}); IN PLACE of the ward's head row when editing
  // ({mode:'edit'}) so the card morphs into the form and its children stay
  // visible below. One element, one position per render (add XOR edit).
  const wardFormPanel = wardForm && (
    <div className="pcp-add-wrap">
      <div className="pcp-add-title">
        {wardForm.mode === 'add' ? 'New ward' : `Edit ward · ${wardOrig?.name || ''}`}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Name *</label>
          <input
            className={inputCls}
            aria-label="Ward name"
            value={wardRow.name}
            onChange={(e) => setWardRow((r) => ({ ...r, name: e.target.value }))}
            placeholder="General Ward"
          />
          {wardErr.name && <div className="text-xs text-rose-600 mt-1">{wardErr.name}</div>}
        </div>
      </div>

      {/* B6 / S2 — "Visible to" departments (optional, multi — the shared B5
          catalog picker). Hidden on a dark org. Empty = org-wide. Saved via the
          departments PUT in the SAME Save click; a name-only save never fires it
          (dead tags never auto-cleared). */}
      {hasDepartments && (
        <div className="mt-3">
          <label className={labelCls}>
            Visible to{' '}
            <span className="text-xs font-normal normal-case text-slate-400 dark:text-slate-500">
              (optional, multi — leave empty for org-wide)
            </span>
          </label>
          <div className="flex flex-wrap gap-4">
            {activeDepts.map((d) => (
              <label key={d.dept_id} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={wardDeptIds.includes(d.dept_id)}
                  onChange={() => toggleWardDept(d.dept_id)}
                />
                {d.name}
              </label>
            ))}
          </div>
          {wardDeptIds.length === 0 && (
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {wardForm.mode === 'edit' && wardOrig?.owner_scope === 'DEPARTMENT'
                ? 'Tagged departments were retired — this ward is now organisation-wide.'
                : 'Visible to everyone (org-wide)'}
            </div>
          )}
        </div>
      )}
      <div className="flex gap-2 mt-4">
        <button className="pcp-btn pcp-btn-primary" onClick={submitWard} disabled={busy}>
          {busy ? 'Saving…' : wardForm.mode === 'add' ? 'Create' : 'Save Changes'}
        </button>
        <button className="pcp-btn pcp-btn-secondary" onClick={closeWard} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );

  // B6 / S4 (Variant A) — the room add/edit form, scoped to `ward`. Rendered as
  // the FIRST child of its ward for an add, and inline BELOW the room row for an
  // edit. Category picker unions over the ward's dept-SET (FE-3).
  const renderRoomForm = (ward) => (
    <div className="pcp-add-wrap">
      <div className="pcp-add-title">
        {roomForm.mode === 'add'
          ? 'New room'
          : `Edit ${roomOrig?.is_bay ? 'bay' : 'room'} · ${roomOrig?.name || ''}`}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>{roomOrig?.is_bay ? 'Bay name *' : 'Room name / number *'}</label>
          <input
            className={inputCls}
            aria-label="Room name"
            value={roomRow.name}
            onChange={(e) => setRoomRow((r) => ({ ...r, name: e.target.value }))}
            placeholder="101"
          />
          {roomErr.name && <div className="text-xs text-rose-600 mt-1">{roomErr.name}</div>}
        </div>
        <div>
          <label className={labelCls}>Bed category *</label>
          {hasCategories ? (
            <select
              className={inputCls}
              aria-label="Bed category"
              value={roomRow.bedcat_id}
              onChange={(e) => setRoomRow((r) => ({ ...r, bedcat_id: e.target.value }))}
            >
              <option value="">Select a category…</option>
              {scopedCategories(ward.department_ids, roomRow.bedcat_id).map((c) => (
                <option key={c.bed_category_id} value={c.bed_category_id}>{c.name}</option>
              ))}
            </select>
          ) : (
            <div className="text-xs text-amber-700 dark:text-amber-300">
              Define a bed category first in <strong>Bed Categories</strong> — a room needs a
              rate before you can add it.
            </div>
          )}
          {roomErr.bedcat_id && <div className="text-xs text-rose-600 mt-1">{roomErr.bedcat_id}</div>}
        </div>
      </div>

      <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">
        Department: inherited from ward ({wardDeptText(ward)})
      </div>

      {/* Locked rate/tags preview — resolved live from the B2 category */}
      {roomPreview ? (
        <div className="mt-2 text-xs rounded-lg px-3 py-2 bg-indigo-50 dark:bg-slate-900 border border-indigo-300 dark:border-indigo-700 text-slate-700 dark:text-slate-200">
          <span className="font-semibold">Rate (locked, from category):</span>{' '}
          {roomPreview.rate}
          {roomPreview.tags.length > 0 && (
            <span> · {roomPreview.tags.join(' · ')}</span>
          )}
          <div className="mt-1">
            Suggested beds: <strong>{suggestBedCount(roomCat)}</strong> (from sharing — editable)
          </div>
        </div>
      ) : (
        <div className="mt-2 text-xs text-slate-400">
          Pick a category to preview its locked rate and suggested bed count.
        </div>
      )}

      <div className="flex gap-2 mt-3">
        <button
          className="pcp-btn pcp-btn-primary"
          onClick={submitRoom}
          disabled={busy || !hasCategories}
        >
          {busy ? 'Saving…' : roomForm.mode === 'add' ? 'Create room' : 'Save Changes'}
        </button>
        <button className="pcp-btn pcp-btn-secondary" onClick={closeRoom} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );

  // Permission gate — all hooks above; safe to early-return now. Redirect home
  // (a missing feature perm shouldn't push toward logout). The server 403s
  // every write regardless (defence-in-depth, S6).
  if (!hasPermission('MANAGE_BED_CONFIG')) {
    return <Navigate to={ROUTES.ORG} replace />;
  }

  return (
    <div className="pcp-page" style={{ background: bg }}>
      <AdminPageHeader />

      <main className="pcp-main">
        <div className="pcp-title-row">
          <div>
            <h1 className="pcp-page-title" style={{ color: isDark ? '#f5f5f7' : '#111827' }}>
              🛏 Bed &amp; Tariff Setup
            </h1>
            <p className="pcp-page-subtitle" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
              Build your organisation&apos;s real ward → room → bed inventory. Beds inherit their
              room&apos;s bed-category and per-day rate.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              className="pcp-btn pcp-btn-secondary"
              onClick={() => navigate(ROUTES.BED_BOARD)}
            >
              Preview board
            </button>
            <button className="pcp-btn pcp-btn-primary" onClick={openAddWard} disabled={formOpen}>
              + Add ward
            </button>
          </div>
        </div>
        <FeesBillingTabs active="bed-setup" />

        {banner && (
          <div className={`pcp-banner ${banner.kind === 'ok' ? 'pcp-banner-ok' : 'pcp-banner-err'}`}>
            <span>{banner.msg}</span>
            <button onClick={() => setBanner(null)} aria-label="Dismiss">✕</button>
          </div>
        )}

        <div className="pcp-filter-bar">
          <label className="pcp-checkbox">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Show deactivated
          </label>
          {hasDepartments && (
            <select
              className={inputCls}
              style={{ width: 'auto' }}
              aria-label="Filter by department"
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
            >
              <option value="">All departments</option>
              <option value={ORG_WIDE_FILTER}>Org-wide</option>
              {activeDepts.map((d) => (
                <option key={d.dept_id} value={d.dept_id}>{d.name}</option>
              ))}
            </select>
          )}
          {/* RC-5 — this is the FILTERED count ("shown"), kept distinct from the
              org totals in the summary strip below (no two competing numbers). */}
          <span className="pcp-count">{visibleTree.length} shown</span>
        </div>

        {/* B7 — organisation config-count summary (single shared selector),
            renders 0/0/0 on a fresh org (a zero is information, never blank). */}
        {!isLoading && !error && (
          <InventoryStatsStrip stats={stats} categoryById={categoryById} />
        )}

        {/* ── New-ward form stays at the TOP (a brand-new item has no anchor
            row). An EDIT morphs the ward's own card in place (below). ──── */}
        {wardForm?.mode === 'add' && wardFormPanel}

        {/* ── Tree ─────────────────────────────────────────────────── */}
        {isLoading ? (
          <div className="pcp-state">Loading…</div>
        ) : error ? (
          <div className="pcp-state pcp-state-err">
            {error}
            <button
              className="pcp-btn pcp-btn-secondary"
              onClick={() => { clearError(); hydrate({ force: true }); }}
            >
              Retry
            </button>
          </div>
        ) : tree.length === 0 ? (
          <div className="pcp-state">
            🛏 No wards yet. Click <strong>+ Add ward</strong> to model your first ward.
          </div>
        ) : visibleTree.length === 0 ? (
          <div className="pcp-state">No wards match this department filter.</div>
        ) : (
          <div className="flex flex-col gap-3">
            {visibleTree.map(({ ward, rooms: wardRooms, bays }) => {
              const isOpen = expanded[ward.ward_id] !== false; // default open
              const deptView = wardDeptView(ward);
              // B7 — per-ward bed count from the shared selector (active-only;
              // present only for active wards, so a deactivated ward shows no
              // fake count). A zero on an active ward is real info (empty ward).
              const wardStat = stats.perWard[ward.ward_id];
              // Variant A — this ward is being edited: its card morphs into the
              // form in place; the children below stay visible for context.
              const isWardEditing = wardForm?.mode === 'edit' && wardForm.id === ward.ward_id;
              const childrenOpen = isOpen || isWardEditing;
              return (
                <div
                  key={ward.ward_id}
                  className={`rounded-xl border p-3 ${ward.effective_active === false ? 'opacity-70' : ''} bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 ${isWardEditing ? 'pcp-card-editing' : ''}`}
                >
                  {isWardEditing ? wardFormPanel : (
                  <div className="flex items-center justify-between gap-2">
                    <button
                      className="flex items-center gap-2 text-left"
                      onClick={() => setExpanded((s) => ({ ...s, [ward.ward_id]: !isOpen }))}
                      aria-label={`Toggle ${ward.name}`}
                    >
                      <span className="text-slate-500">{isOpen ? '▾' : '▸'}</span>
                      <span className="font-semibold text-slate-800 dark:text-slate-100">{ward.name}</span>
                      {wardStat && (
                        <span className="text-xs font-normal text-slate-500 dark:text-slate-400" data-testid="ward-bed-count">
                          · {wardStat.rooms} room{wardStat.rooms === 1 ? '' : 's'} · {wardStat.beds} bed{wardStat.beds === 1 ? '' : 's'}
                        </span>
                      )}
                      {hasDepartments && (
                        <span className="text-xs text-slate-500 dark:text-slate-400 inline-flex flex-wrap items-center gap-1">
                          · Dept:{' '}
                          {deptView.names.length > 0 ? (
                            deptView.ids.map((id, i) => (
                              <span
                                key={id}
                                className="pcp-chip"
                                style={{
                                  background: isDark ? '#3730a3' : '#e0e7ff',
                                  color: isDark ? '#e0e7ff' : '#3730a3',
                                  borderColor: isDark ? '#4f46e5' : '#a5b4fc',
                                }}
                              >
                                🏷 {deptView.names[i]}
                              </span>
                            ))
                          ) : deptView.retired ? (
                            <span className="text-slate-500 dark:text-slate-400">Org-wide (tagged departments retired)</span>
                          ) : (
                            <span className="pcp-chip">Org-wide</span>
                          )}
                        </span>
                      )}
                      {ward.active === false && (
                        <span className="pcp-chip pcp-chip-inactive">Deactivated</span>
                      )}
                    </button>
                    <div className="pcp-actions">
                      <button className="pcp-btn pcp-btn-link" onClick={() => openEditWard(ward)} disabled={formOpen}>Edit</button>
                      <button className="pcp-btn pcp-btn-link" onClick={() => openAddRoom(ward.ward_id)} disabled={formOpen}>+ Add room</button>
                      <button className="pcp-btn pcp-btn-link" onClick={() => openAddBay(ward.ward_id)} disabled={formOpen}>+ Add beds directly (open ward / bay)</button>
                      {ward.active === false
                        ? <button className="pcp-btn pcp-btn-link" onClick={() => reactivateWard(ward.ward_id)} disabled={formOpen}>Reactivate</button>
                        : <button className="pcp-btn pcp-btn-link pcp-btn-danger-link" onClick={() => askDeactivateWard(ward)} disabled={formOpen}>Deactivate</button>}
                    </div>
                  </div>
                  )}

                  {childrenOpen && (
                    <div className="mt-3 pl-5 flex flex-col gap-2">
                      {/* + Add room form — the FIRST child of its ward (above bays) */}
                      {roomForm && roomForm.mode === 'add' && roomForm.wardId === ward.ward_id && (
                        renderRoomForm(ward)
                      )}
                      {/* Bay flow: pick a category → creates an is_bay room + beds */}
                      {bedForm && bedForm.mode === 'addBay' && bedForm.wardId === ward.ward_id && (
                        <div className="pcp-add-wrap">
                          <div className="pcp-add-title">Add beds directly (open ward / bay)</div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                              <label className={labelCls}>
                                Bay name{' '}
                                {baySecondNeedsName ? '*' : (
                                  <span className="text-xs font-normal normal-case text-slate-400 dark:text-slate-500">
                                    (optional — defaults to &ldquo;General Bay&rdquo;)
                                  </span>
                                )}
                              </label>
                              <input
                                className={inputCls}
                                aria-label="Bay name"
                                value={bedRow.label}
                                onChange={(e) => setBedRow((r) => ({ ...r, label: e.target.value }))}
                                placeholder="General Bay"
                              />
                              {bedErr.label && <div className="text-xs text-rose-600 mt-1">{bedErr.label}</div>}
                            </div>
                            <div>
                              <label className={labelCls}>Bed category *</label>
                              {hasCategories ? (
                                <select
                                  className={inputCls}
                                  aria-label="Bay bed category"
                                  value={bedRow.bedcat_id}
                                  onChange={(e) => {
                                    const cat = categoryById[e.target.value];
                                    setBedRow((r) => ({ ...r, bedcat_id: e.target.value, count: suggestBedCount(cat) }));
                                  }}
                                >
                                  <option value="">Select a category…</option>
                                  {scopedCategories(ward.department_ids, bedRow.bedcat_id).map((c) => (
                                    <option key={c.bed_category_id} value={c.bed_category_id}>{c.name}</option>
                                  ))}
                                </select>
                              ) : (
                                <div className="text-xs text-amber-700 dark:text-amber-300">
                                  Define a bed category first — a bay bed needs a rate.
                                </div>
                              )}
                              {bedErr.bedcat_id && <div className="text-xs text-rose-600 mt-1">{bedErr.bedcat_id}</div>}
                            </div>
                            <div>
                              <label className={labelCls}>How many beds?</label>
                              <input
                                className={inputCls}
                                type="number"
                                min="1"
                                max="50"
                                aria-label="Bay bed count"
                                value={bedRow.count}
                                onChange={(e) => setBedRow((r) => ({ ...r, count: e.target.value }))}
                              />
                            </div>
                          </div>
                          {bedFormPreview ? (
                            <div className="mt-2 text-xs rounded-lg px-3 py-2 bg-indigo-50 dark:bg-slate-900 border border-indigo-300 dark:border-indigo-700 text-slate-700 dark:text-slate-200">
                              <span className="font-semibold">Rate (locked, from category):</span>{' '}
                              {bedFormPreview.rate}
                              {bedFormPreview.tags.length > 0 && <span> · {bedFormPreview.tags.join(' · ')}</span>}
                            </div>
                          ) : (
                            <div className="mt-2 text-xs text-slate-400">
                              Pick a category to preview its locked rate.
                            </div>
                          )}
                          <div className="flex gap-2 mt-3">
                            <button className="pcp-btn pcp-btn-primary" onClick={submitBed} disabled={busy || !hasCategories}>
                              {busy ? 'Saving…' : 'Create bay + beds'}
                            </button>
                            <button className="pcp-btn pcp-btn-secondary" onClick={closeBed} disabled={busy}>Cancel</button>
                          </div>
                        </div>
                      )}

                      {/* Bays: beds render directly under the ward */}
                      {bays.map(({ room, beds: bayBeds }) => {
                        const cat = categoryById[room.bedcat_id];
                        const prev = categoryPreview(cat);
                        // Variant A — a bay renames via the SAME room-edit form/path
                        // (updateRoom); its form expands inline below the bay row.
                        const isBayEditing = roomForm?.mode === 'edit' && roomForm.id === room.room_id;
                        return (
                          <React.Fragment key={room.room_id}>
                          <div
                            className={`rounded-lg border p-2 ${room.effective_active === false ? 'opacity-70' : ''} bg-amber-50 dark:bg-slate-900 border-amber-300 dark:border-slate-700 ${isBayEditing ? 'pcp-card-editing' : ''}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div>
                                <span className="pcp-chip">Bay</span>
                                <span className="font-medium text-slate-800 dark:text-slate-100 ml-2">{room.name}</span>
                                <span className="text-xs text-slate-500 dark:text-slate-400 ml-2">
                                  {cat ? cat.name : '— (category unavailable)'}
                                  {prev && ` · ${prev.rate}`}
                                </span>
                                <span className="text-xs text-slate-400 ml-2">{bayBeds.length} beds</span>
                                {room.active === false && <span className="pcp-chip pcp-chip-inactive ml-2">Deactivated</span>}
                              </div>
                              <div className="pcp-actions">
                                <button className="pcp-btn pcp-btn-link" onClick={() => openEditRoom(ward.ward_id, room)} disabled={formOpen}>Rename</button>
                                {room.active === false
                                  ? <button className="pcp-btn pcp-btn-link" onClick={() => reactivateRoom(ward.ward_id, room.room_id)} disabled={formOpen}>Reactivate</button>
                                  : <button className="pcp-btn pcp-btn-link pcp-btn-danger-link" onClick={() => askDeactivateRoom(ward.ward_id, room)} disabled={formOpen}>Deactivate</button>}
                              </div>
                            </div>
                            {renderBedSection(ward.ward_id, room, bayBeds, { isBay: true })}
                          </div>
                          {isBayEditing && renderRoomForm(ward)}
                          </React.Fragment>
                        );
                      })}

                      {wardRooms.length === 0 && (!roomForm || roomForm.wardId !== ward.ward_id) && (
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          No rooms yet — click <strong>+ Add room</strong>.
                        </div>
                      )}

                      {wardRooms.map(({ room, beds: roomBeds }) => {
                        const cat = categoryById[room.bedcat_id];
                        const prev = categoryPreview(cat);
                        // Variant A — this room is being edited: its form expands
                        // inline directly below the room row.
                        const isRoomEditing = roomForm?.mode === 'edit' && roomForm.id === room.room_id;
                        return (
                          <React.Fragment key={room.room_id}>
                          <div
                            className={`rounded-lg border p-2 ${room.effective_active === false ? 'opacity-70' : ''} bg-slate-50 dark:bg-slate-900 border-slate-300 dark:border-slate-700 ${isRoomEditing ? 'pcp-card-editing' : ''}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div>
                                <span className="font-medium text-slate-800 dark:text-slate-100">{room.name}</span>
                                <span className="text-xs text-slate-500 dark:text-slate-400 ml-2">
                                  {cat ? cat.name : '— (category unavailable)'}
                                  {prev && ` · ${prev.rate}`}
                                </span>
                                <span className="text-xs text-slate-400 ml-2">{roomBeds.length} beds</span>
                                {room.active === false && (
                                  <span className="pcp-chip pcp-chip-inactive ml-2">Deactivated</span>
                                )}
                              </div>
                              <div className="pcp-actions">
                                <button className="pcp-btn pcp-btn-link" onClick={() => openEditRoom(ward.ward_id, room)} disabled={formOpen}>Edit</button>
                                {room.active === false
                                  ? <button className="pcp-btn pcp-btn-link" onClick={() => reactivateRoom(ward.ward_id, room.room_id)} disabled={formOpen}>Reactivate</button>
                                  : <button className="pcp-btn pcp-btn-link pcp-btn-danger-link" onClick={() => askDeactivateRoom(ward.ward_id, room)} disabled={formOpen}>Deactivate</button>}
                              </div>
                            </div>
                            {renderBedSection(ward.ward_id, room, roomBeds)}
                          </div>
                          {isRoomEditing && renderRoomForm(ward)}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {confirm && (
        <ConfirmationModal
          isOpen={true}
          title={confirm.title}
          message={confirm.message}
          ctaLabel="Deactivate"
          cancelLabel="Cancel"
          variant="danger"
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
